// Paper Trading Service - Full Persistence & Real-time Simulation Engine

const STORAGE_PREFIX = "minopro_paper_trading_";

export const DEFAULT_PAPER_ACCOUNT = {
  accountId: "SIM-DEMO",
  initialBalance: 10000.0,
  balance: 10000.0,
  positions: [],
  tradeHistory: [],
  lastUpdated: Date.now(),
};

/**
 * Get pip multiplier and contract size for realistic PnL calculation
 */
export function getSymbolMultiplier(symbol = "XAUUSD") {
  const sym = (symbol || "").toUpperCase();
  if (sym.includes("XAU") || sym.includes("GOLD")) {
    return { multiplier: 100, pipDecimals: 1, pipSize: 0.1 }; // Gold 1 lot = 100 oz ($1 move = $100)
  }
  if (sym.includes("BTC")) {
    return { multiplier: 1, pipDecimals: 0, pipSize: 1.0 };   // BTC 1 lot = 1 BTC
  }
  if (sym.includes("ETH")) {
    return { multiplier: 1, pipDecimals: 1, pipSize: 0.1 };   // ETH 1 lot = 1 ETH
  }
  if (sym.includes("US30") || sym.includes("USTEC") || sym.includes("NAS100")) {
    return { multiplier: 1, pipDecimals: 0, pipSize: 1.0 };   // Indices
  }
  if (sym.includes("JPY")) {
    return { multiplier: 1000, pipDecimals: 2, pipSize: 0.01 }; // JPY pairs
  }
  return { multiplier: 100000, pipDecimals: 4, pipSize: 0.0001 }; // Standard Forex (EURUSD, GBPUSD)
}

/**
 * Calculate floating PnL for a position
 */
export function calculatePositionPnL(pos, currentPrice) {
  if (!currentPrice || currentPrice <= 0 || !pos) {
    return { pnl: 0, pnlPips: 0 };
  }

  const { multiplier, pipSize } = getSymbolMultiplier(pos.symbol);
  const priceDiff = pos.type === "BUY" ? (currentPrice - pos.entryPrice) : (pos.entryPrice - currentPrice);
  const pnl = Math.round(priceDiff * pos.lotSize * multiplier * 100) / 100;
  const pnlPips = Math.round((priceDiff / pipSize) * 10) / 10;

  return { pnl, pnlPips };
}

/**
 * Load paper trading account for specific user
 */
export function loadPaperAccount(userId = "guest") {
  const defaultAccId = `SIM-${(userId || "guest").toString().slice(-6).toUpperCase()}`;
  try {
    const key = `${STORAGE_PREFIX}${userId || "guest"}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        accountId: parsed.accountId || defaultAccId,
        initialBalance: Number(parsed.initialBalance) || 10000.0,
        balance: Number(parsed.balance) !== undefined && !isNaN(Number(parsed.balance)) ? Number(parsed.balance) : 10000.0,
        positions: Array.isArray(parsed.positions) ? parsed.positions : [],
        tradeHistory: Array.isArray(parsed.tradeHistory) ? parsed.tradeHistory : [],
        lastUpdated: parsed.lastUpdated || Date.now(),
      };
    }
  } catch (e) {
    console.warn("Error loading paper account:", e);
  }
  return { 
    ...DEFAULT_PAPER_ACCOUNT,
    accountId: defaultAccId,
  };
}

/**
 * Save paper trading account
 */
export function savePaperAccount(userId = "guest", accountData = {}) {
  try {
    const key = `${STORAGE_PREFIX}${userId || "guest"}`;
    const toSave = {
      accountId: accountData.accountId || `SIM-${(userId || "guest").toString().slice(-6).toUpperCase()}`,
      initialBalance: Number(accountData.initialBalance) || 10000.0,
      balance: Math.round((Number(accountData.balance) || 0) * 100) / 100,
      positions: Array.isArray(accountData.positions) ? accountData.positions : [],
      tradeHistory: Array.isArray(accountData.tradeHistory) ? accountData.tradeHistory.slice(0, 200) : [],
      lastUpdated: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(toSave));
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("minopro_paper_account_updated", { 
        detail: { userId: userId || "guest", account: toSave } 
      }));
    }
    return toSave;
  } catch (e) {
    console.warn("Error saving paper account:", e);
    return accountData;
  }
}

/**
 * Reset account back to initial capital or custom amount
 */
export function resetPaperAccount(userId = "guest", newInitialBalance = 10000.0, clearHistory = false) {
  const current = loadPaperAccount(userId);
  const initial = Number(newInitialBalance) || 10000.0;
  const resetData = {
    initialBalance: initial,
    balance: initial,
    positions: [],
    tradeHistory: clearHistory ? [] : current.tradeHistory,
    lastUpdated: Date.now(),
  };
  return savePaperAccount(userId, resetData);
}

/**
 * Deposit / Add funds to existing balance without resetting trades
 */
export function depositPaperFunds(userId = "guest", amount = 1000.0) {
  const current = loadPaperAccount(userId);
  const addAmt = Number(amount) || 0;
  if (addAmt <= 0) return current;

  const updated = {
    ...current,
    balance: Math.round((current.balance + addAmt) * 100) / 100,
    lastUpdated: Date.now(),
  };
  return savePaperAccount(userId, updated);
}

/**
 * Open a new position
 */
export function openPaperPosition(userId = "guest", { symbol, type, lotSize, entryPrice, sl = null, tp = null }) {
  const current = loadPaperAccount(userId);
  const newPos = {
    id: "pos_" + Date.now() + "_" + Math.random().toString(36).substring(2, 6),
    symbol: symbol || "XAUUSD",
    type: type === "SELL" ? "SELL" : "BUY",
    lotSize: Number(lotSize) || 0.1,
    entryPrice: Number(entryPrice),
    sl: sl ? Number(sl) : null,
    tp: tp ? Number(tp) : null,
    openTime: new Date().toISOString(),
    openTimeDisplay: new Date().toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
  };

  const updated = {
    ...current,
    positions: [newPos, ...current.positions],
    lastUpdated: Date.now(),
  };

  return savePaperAccount(userId, updated);
}

/**
 * Close an existing position
 */
export function closePaperPosition(userId = "guest", posId, closePrice, closeReason = "MANUAL") {
  const current = loadPaperAccount(userId);
  const pos = current.positions.find((p) => p.id === posId);
  if (!pos) return current;

  const { pnl, pnlPips } = calculatePositionPnL(pos, closePrice);
  const newBalance = Math.round((current.balance + pnl) * 100) / 100;

  const closedRecord = {
    ...pos,
    closePrice: Number(closePrice),
    closeTime: new Date().toISOString(),
    closeTimeDisplay: new Date().toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    pnl,
    pnlPips,
    closeReason, // "MANUAL" | "TP" | "SL"
    result: pnl > 0 ? "WIN" : pnl < 0 ? "LOSS" : "BE",
  };

  const updated = {
    ...current,
    balance: newBalance,
    positions: current.positions.filter((p) => p.id !== posId),
    tradeHistory: [closedRecord, ...current.tradeHistory].slice(0, 200),
    lastUpdated: Date.now(),
  };

  return savePaperAccount(userId, updated);
}

/**
 * Check and auto-trigger SL / TP on live tick updates
 */
export function checkAutoSlTp(userId = "guest", currentSymbol, currentPrice) {
  if (!currentPrice || currentPrice <= 0) return null;

  const current = loadPaperAccount(userId);
  let hasTriggered = false;
  let updatedAccount = { ...current };

  for (const pos of current.positions) {
    if (pos.symbol !== currentSymbol) continue;

    let shouldClose = false;
    let closeReason = "MANUAL";

    if (pos.type === "BUY") {
      if (pos.tp && currentPrice >= pos.tp) {
        shouldClose = true;
        closeReason = "TP";
      } else if (pos.sl && currentPrice <= pos.sl) {
        shouldClose = true;
        closeReason = "SL";
      }
    } else if (pos.type === "SELL") {
      if (pos.tp && currentPrice <= pos.tp) {
        shouldClose = true;
        closeReason = "TP";
      } else if (pos.sl && currentPrice >= pos.sl) {
        shouldClose = true;
        closeReason = "SL";
      }
    }

    if (shouldClose) {
      updatedAccount = closePaperPosition(userId, pos.id, currentPrice, closeReason);
      hasTriggered = true;
    }
  }

  return hasTriggered ? updatedAccount : null;
}

/**
 * Compute performance statistics from trade history
 */
export function getPaperStatistics(tradeHistory = []) {
  if (!tradeHistory || tradeHistory.length === 0) {
    return {
      totalTrades: 0,
      totalClosed: 0,
      wins: 0,
      winTrades: 0,
      losses: 0,
      lossTrades: 0,
      breakeven: 0,
      winRate: 0,
      grossProfit: 0,
      grossLoss: 0,
      netPnL: 0,
      totalPnL: 0,
      profitFactor: 0,
    };
  }

  let wins = 0;
  let losses = 0;
  let breakeven = 0;
  let grossProfit = 0;
  let grossLoss = 0;

  tradeHistory.forEach((t) => {
    const pnl = Number(t.pnl) || 0;
    if (pnl > 0) {
      wins++;
      grossProfit += pnl;
    } else if (pnl < 0) {
      losses++;
      grossLoss += Math.abs(pnl);
    } else {
      breakeven++;
    }
  });

  const totalTrades = tradeHistory.length;
  const winRate = totalTrades > 0 ? Math.round((wins / totalTrades) * 1000) / 10 : 0;
  const netPnL = Math.round((grossProfit - grossLoss) * 100) / 100;
  const profitFactor = grossLoss > 0 ? Math.round((grossProfit / grossLoss) * 100) / 100 : (grossProfit > 0 ? 99.0 : 1.0);

  return {
    totalTrades,
    totalClosed: totalTrades,
    wins,
    winTrades: wins,
    losses,
    lossTrades: losses,
    breakeven,
    winRate,
    grossProfit: Math.round(grossProfit * 100) / 100,
    grossLoss: Math.round(grossLoss * 100) / 100,
    netPnL,
    totalPnL: netPnL,
    profitFactor,
  };
}

// Cross-tab real-time sync for Paper Trading accounts across multiple browser windows
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key && e.key.startsWith(STORAGE_PREFIX) && e.newValue) {
      try {
        const userId = e.key.replace(STORAGE_PREFIX, "");
        const account = JSON.parse(e.newValue);
        window.dispatchEvent(new CustomEvent("minopro_paper_account_updated", {
          detail: { userId, account }
        }));
      } catch (err) {}
    }
  });
}
